import {getCountryCode, getLanguage} from '@smart/locale-service';
import {Button, Notification} from '@smart/react-components';
import type {FormEventHandler} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import FormStyles from '../../common/form-styles.module.scss';
import {scrollToRef} from '../../common/helpers';
import type {
  CountryCode,
  LangCode,
  ViewsFetchingToPostForm,
  availableCountries,
} from '../../common/types';
import {AmountOfCarSelect} from '../../components/amount-of-car-select';
import {AppModelContext} from '../../components/app';
import {CountrySelect} from '../../components/country-select';
import Intercept from '../../components/easy-form/Intercept';
import Fields from '../../components/easy-form/fields';
import type {WrapperHandler} from '../../components/easy-form/types';
import {LegalLinks} from '../../components/legal-links';
import {MktLangSelect} from '../../components/mkt-lang-select';
import MultiRadio from '../../components/multi-radio';
import PhoneCodeSelect from '../../components/phone-code-select';
import PhoneField from '../../components/phone-field';
import {useAPI} from '../../hooks/use-api';
import {useEntry} from '../../hooks/use-entry';
import {useLocale} from '../../hooks/use-locale';
import {useTracking} from '../../hooks/use-tracking';
import type {ITrackFleet} from '../../hooks/use-tracking/types';
import {useViews} from '../../hooks/use-views';
import {useSchemas} from '../../hooks/user-schemas';
import type {Success} from '../success';
import fleetStyles from './styles.module.scss';

export interface IFleetFormState {
  personalInfo: {
    salutation: string;
    firstName: string;
    lastName: string;
    market: CountryCode;
  };
  contact: {
    email: string;
    phoneCode?: string;
    phoneNumber?: string;
    country: CountryCode;
    city: string;
    postalCode: string;
    preferredCommunicationLanguage: LangCode;
  };
  companyInformation: {
    companyName: string;
    position: string;
    totalFleetSize: string;
    amountOfCars?: string;
    daimlerFrameworkAgreement: 'Yes' | 'No';
    descriptionField: string;
  };
}

const viewName: ViewsFetchingToPostForm = `fleet-form`;

export default function ContactPrefs(): JSX.Element {
  const [hasErrors, setHasErrors] = useState(true);
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [formData, setFormData] = useState<IFleetFormState>();
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const [mkt, setMkt] = useState<CountryCode>();
  const apiFetch = useAPI(viewName, true);
  const {setView} = useViews();
  const generalSchemas = useSchemas();
  const [entry] = useEntry();
  const [trackStep, successTrack] = useTracking<ITrackFleet>(`fleet`, `fleet`);
  const {languageTag, marketId} = useLocale();
  const countryCode = getCountryCode(marketId);
  const language = getLanguage(languageTag);

  const cmsTranslations =
    React.useContext(AppModelContext).cms.formsFeatureAppFleetUi || {};
  const apiTranslations =
    React.useContext(AppModelContext).cms.apiErrorMessageKeys || {};
  // UPDATE WHEN ERROR CODES ARE AVAILABLE FROM API
  // const cmsTranslationsErrors =
  //   React.useContext(AppModelContext).cms.formsFeatureAppValidation_ErrorsUi ||
  //   {};
  // const cfmError = cmsTranslationsErrors.validations_serverError?.toString();
  const formWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (entry === viewName) {
      trackStep(1, `form_start`);
    }
  }, []);

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (hasErrors) {
      setSubmitAttempt(true);
      scrollToRef(formWrapperRef);
      return;
    }

    if (!formData) return;

    setLoading(true);
    setServerError(undefined);

    try {
      trackStep(1, `form_engagement`);
      if (
        formData.contact.phoneCode === `` &&
        formData.contact.phoneNumber === ``
      ) {
        delete formData.contact.phoneCode;
        delete formData.contact.phoneNumber;
      } else if (formData.companyInformation.amountOfCars === ``) {
        delete formData.companyInformation.amountOfCars;
      }
      await apiFetch(formData);
      successTrack(2, `success-fleet`);

      setView<typeof Success>(`success`, {
        successGreeting: cmsTranslations.fleet_successPage_greeting,
        successTitle: cmsTranslations.fleet_successPage_title,
      });
    } catch (error) {
      setServerError(error as string);
      setLoading(false);
      scrollToRef(formWrapperRef);
    }
  };

  const changeHandler: WrapperHandler<IFleetFormState> = (state, errors) => {
    setHasErrors(Boolean(errors));
    setFormData(state as unknown as IFleetFormState);

    const selectedMKT = (state as IFleetFormState).contact?.country;

    if (!!selectedMKT) {
      setMkt(selectedMKT);
    } else if (!!mkt) {
      setMkt(undefined);
    }
  };

  const schemas = useMemo(() => {
    const {
      subject,
      email,
      largeText,
      firstAndLastNames,
      city,
      postalCode,
      companyName,
      position,
    } = generalSchemas;
    return {
      firstName: firstAndLastNames(true),
      lastName: firstAndLastNames(true),
      email: email(true),
      subject: subject(true),
      descriptionField: largeText(undefined),
      city: city(true),
      postalCode: postalCode(formData?.contact?.country, true),
      companyName: companyName(true),
      position: position(false),
      totalFleetSize: largeText(true),
    };
  }, [generalSchemas, formData?.contact?.country]);

  const setValueFromAEM = () => {
    let apiErrorMessage = ``;
    let translatedString;

    const getStringFromAEM = apiTranslations.errorMessageKeys?.filter((e) => {
      return e?.includes(serverError as any);
    }, serverError);

    if (!getStringFromAEM) {
      apiErrorMessage = apiTranslations.generalFallbackErrorMessage as string;
    } else {
      translatedString = getStringFromAEM && getStringFromAEM[0]?.split(`:`);
      apiErrorMessage = translatedString && (translatedString[1] as any);
    }
    return apiErrorMessage;
  };

  return (
    <div className={FormStyles.wrapper} ref={formWrapperRef}>
      {serverError && (
        <Notification
          signal="error"
          className={FormStyles.serverError}
          onOpenChange={(open) => {
            if (!open) {
              setServerError(undefined);
            }
          }}
        >
          <Notification.Text>{setValueFromAEM()}</Notification.Text>
        </Notification>
      )}

      <form onSubmit={onSubmit}>
        <Fields.Wrapper
          translationPrefix={viewName}
          onUpdate={changeHandler}
          showErrors={submitAttempt}
          disabled={loading}
        >
          <Intercept
            set
            tag="personalInfo"
            label={cmsTranslations.fleet_PersonalInformation}
          >
            <Fields.Text
              tag="firstName"
              required
              schema={schemas.firstName}
              label={cmsTranslations.fleet_personalInformation_firstName}
            />

            <Fields.Text
              tag="lastName"
              required
              schema={schemas.firstName}
              label={cmsTranslations.fleet_personalInformation_lastName}
            />
          </Intercept>
          <Intercept set tag="contact" label={cmsTranslations.fleet_contact}>
            <Fields.Text
              tag="email"
              required
              schema={schemas.email}
              label={cmsTranslations.fleet_contact_email}
            />

            <div className={FormStyles.phoneFieldContainer}>
              <PhoneCodeSelect
                tag="phoneCode"
                props={{className: FormStyles.phoneCode}}
                label={cmsTranslations.fleet_contact_PhoneCode}
              />
              <PhoneField
                tag="phoneNumber"
                props={{className: FormStyles.phoneNumber}}
                label={cmsTranslations.fleet_contact_phoneNumber}
              />
            </div>

            <CountrySelect
              required
              tag="country"
              initial={countryCode}
              label={cmsTranslations.fleet_contact_market}
            />

            <div className={fleetStyles.MktLangSelect}>
              <MktLangSelect
                initial={language}
                mkt={countryCode as (typeof availableCountries)[number]}
                tag="preferredCommunicationLanguage"
                required
                label={cmsTranslations.fleet_contact_preferredLanguage}
              />
            </div>

            <Fields.Text
              tag="city"
              required
              schema={schemas.city}
              label={cmsTranslations.fleet_contact_city}
            />

            <Fields.Text
              tag="postalCode"
              required
              schema={schemas.postalCode}
              label={cmsTranslations.fleet_contact_postalCode}
            />
          </Intercept>

          <Intercept
            set
            tag="companyInformation"
            label={cmsTranslations.fleet_companyInformation}
          >
            <Fields.Text
              tag="companyName"
              required
              schema={schemas.companyName}
              label={cmsTranslations.fleet_companyInformation_companyName}
            />

            <Fields.Text
              tag="position"
              schema={schemas.position}
              label={cmsTranslations.fleet_companyInformation_position}
            />

            <AmountOfCarSelect tag="totalFleetSize" required />

            <AmountOfCarSelect tag="amountOfCars" />

            <div className={fleetStyles.multiRadio}>
              <MultiRadio
                options={[`Yes`, `No`]}
                tag="daimlerFrameworkAgreement"
                required
                optionsLabel={[
                  cmsTranslations.fleet_options_yes || ``,
                  cmsTranslations.fleet_options_No || ``,
                ]}
                label={
                  cmsTranslations.fleet_companyInformation_frameworkAgreement ||
                  ``
                }
              />
            </div>

            <Fields.Area
              tag="descriptionField"
              max={1024}
              schema={schemas.descriptionField}
              label={cmsTranslations.fleet_companyInformation_description}
            />

            <LegalLinks
              className={FormStyles.legal}
              legalText={cmsTranslations.legal}
            />
          </Intercept>
          <Button
            type="submit"
            variant="primary"
            className={FormStyles.button}
            disabled={(submitAttempt && hasErrors) || loading}
          >
            {cmsTranslations.fleet_submit || `Submit`}
          </Button>
        </Fields.Wrapper>
      </form>
    </div>
  );
}
